// Path: ./src/app/pages/ElasticTrigger/engagement/errors.ts

/**
 * Normalized failure from an /admin-engagement call.
 *
 * The engagement endpoints propagate real HTTP errors (unlike the cron, which
 * alerts silently), so the panel surfaces `message` verbatim rather than a
 * generic "contact administrator" string.
 */
export type EngagementApiError = {
  /** HTTP status, when the request reached the server and got a response. */
  status?: number;
  /** Server-supplied message where available, otherwise the transport failure. */
  message: string;
  /**
   * True when the request never reached a verdict — a gateway timeout, a
   * dropped connection, or our own client-side abort. The server may still be
   * working; the caller must not report this as "the run stopped".
   */
  inconclusive: boolean;
};

/** Statuses returned by a proxy that gave up before the server did. */
const GATEWAY_STATUSES = [408, 502, 503, 504];

type ResponseLike = {
  status: number;
  statusText?: string;
  json: () => Promise<unknown>;
};

/** The generated SDK throws the raw `Response` on any non-2xx. */
const isResponseLike = (error: unknown): error is ResponseLike => {
  if (typeof error !== "object" || error === null) return false;
  const candidate = error as Partial<ResponseLike>;
  return (
    typeof candidate.status === "number" && typeof candidate.json === "function"
  );
};

/**
 * Turns whatever the generated SDK threw into an {@link EngagementApiError}.
 *
 * The SDK throws the raw `Response` on a non-2xx, and `fetch` rejects with a
 * `TypeError` when the connection itself fails.
 */
export const toEngagementApiError = async (
  error: unknown,
  fallbackMessage = "The request failed.",
): Promise<EngagementApiError> => {
  if (isResponseLike(error)) {
    const { status } = error;
    let message = "";
    try {
      // Nest surfaces validation failures as `message: string[]`.
      const body = (await error.json()) as {
        message?: string | string[];
        error?: string;
      };
      const raw = body?.message ?? body?.error;
      message = Array.isArray(raw) ? raw.join(", ") : (raw ?? "");
    } catch {
      // non-JSON error body (an HTML gateway page, most likely)
    }

    if (!message) {
      message =
        status === 401
          ? "Not authenticated. Sign in again."
          : status === 403
            ? "This action requires a SuperAdmin account."
            : `Request failed with HTTP ${status}${
                error.statusText ? ` ${error.statusText}` : ""
              }.`;
    }

    return {
      status,
      message,
      inconclusive: GATEWAY_STATUSES.includes(status),
    };
  }

  // Our own AbortController fired, or the browser dropped the connection.
  const name = (error as { name?: string })?.name;
  if (name === "AbortError") {
    return {
      message: "The client stopped waiting for a response.",
      inconclusive: true,
    };
  }
  if (error instanceof TypeError) {
    return {
      message: `Could not reach the server: ${error.message}`,
      inconclusive: true,
    };
  }

  const message = (error as { message?: string })?.message;
  return { message: message || fallbackMessage, inconclusive: false };
};
